import React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { StageRun, StageStatus } from "../types";

const STAGE_LABELS: Record<string, string> = {
  dependency_check: "Dependency check",
  data_extraction: "Data extraction",
  report_generation: "Report generation",
  distribution: "Distribution",
};

const COLORS: Record<StageStatus, string> = {
  success: "#1f9d57",
  failed: "#d64550",
  running: "#3b82c4",
  skipped: "#b0b6c3",
  pending: "#d3d7e0",
};

function stageIcon(status: StageStatus) {
  switch (status) {
    case "success":
      return <CheckRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />;
    case "failed":
      return <CloseRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />;
    case "running":
      return (
        <AutorenewRoundedIcon
          sx={{
            fontSize: 16,
            color: "#fff",
            animation: "awbr-spin 1.2s linear infinite",
            "@keyframes awbr-spin": { to: { transform: "rotate(360deg)" } },
          }}
        />
      );
    default:
      return <RemoveRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />;
  }
}

/**
 * Horizontal stage pipeline visualization for a run, mirroring the canonical
 * order: dependency_check -> data_extraction -> report_generation -> distribution.
 */
const StagePipeline: React.FC<{ stages: StageRun[]; compact?: boolean }> = ({
  stages,
  compact = false,
}) => {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0 }}
    >
      {stages.map((stage, i) => (
        <React.Fragment key={stage.name}>
          <Tooltip
            title={`${STAGE_LABELS[stage.name]} · ${stage.status}${
              stage.durationSec != null ? ` · ${stage.durationSec}s` : ""
            }`}
            arrow
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: COLORS[stage.status],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow:
                    stage.status === "running"
                      ? "0 0 0 4px rgba(59,130,196,0.18)"
                      : "none",
                }}
              >
                {stageIcon(stage.status)}
              </Box>
              {!compact && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 84,
                    textAlign: "center",
                  }}
                >
                  {STAGE_LABELS[stage.name]}
                </Typography>
              )}
            </Box>
          </Tooltip>
          {i < stages.length - 1 && (
            <Box
              sx={{
                flex: compact ? "0 0 24px" : "1 1 32px",
                minWidth: 24,
                height: 2,
                mx: 0.5,
                mb: compact ? 0 : 3,
                bgcolor:
                  stages[i + 1].status === "pending"
                    ? "#e2e5ee"
                    : COLORS[stage.status],
                borderRadius: 1,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default StagePipeline;
